// import React from "react";
// import {
//   ComposedChart,
//   Line,
//   Bar,
//   CartesianGrid,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
// } from "recharts";

// export default function DailyTradesChart({ data }) {
//   if (!data || !data.length) return <p>No trade data available.</p>;

//   // Aggregate by day
//   const dailyMap = {};
//   let cumulative = 0;

//   data.forEach((trade) => {
//     const date = new Date(trade.trade_date).toISOString().slice(0, 10);
//     cumulative += Number(trade.cash_return || 0);

//     if (!dailyMap[date]) {
//       dailyMap[date] = { date, dailyReturn: 0, cumulativeReturn: 0 };
//     }

//     dailyMap[date].dailyReturn += Number(trade.cash_return || 0);
//     dailyMap[date].cumulativeReturn = cumulative;
//   });

//   const chartData = Object.values(dailyMap);

//   // Custom tooltip
//   const CustomTooltip = ({ active, payload, label }) => {
//     if (!active || !payload || !payload.length) return null;
//     const daily = payload.find((p) => p.dataKey === "dailyReturn")?.value || 0;
//     const cumulative = payload.find((p) => p.dataKey === "cumulativeReturn")?.value || 0;

//     return (
//       <div className="custom-tooltip" style={{ background: "#fff", padding: 10, border: "1px solid #ccc" }}>
//         <p><strong>{label}</strong></p>
//         <p>
//           Daily Return:{" "}
//           <span style={{ color: daily >= 0 ? "#16a34a" : "#dc2626" }}>
//             ${daily.toFixed(2)}
//           </span>
//         </p>
//         <p>Cumulative Return: {" "}
//           <span style={{ color: cumulative >= 0 ? "#16a34a" : "#dc2626" }}>
//           ${cumulative.toFixed(2)}
//           </span>
//           </p>
//       </div>
//     );
//   };

//   return (
//     <ResponsiveContainer width="100%" height={300}>
//       <ComposedChart data={chartData}>
//         {/* <CartesianGrid stroke="#ccc" /> */}
//         <CartesianGrid vertical={false} stroke="#e5e7eb" />
//         <XAxis 
//           dataKey="date"
//           // interval="preserveStartEnd"
//           interval={4}
//           tick={{ fill: "#6b7280", fontSize: 12 }}
//           tickLine={false}
//           axisLine={false} 
//         />
//         <YAxis 
//         tick={{ fill: "#6b7280", fontSize: 12 }}/>
//         <Tooltip content={<CustomTooltip />} />
//         <Bar
//           dataKey="dailyReturn"
//           barSize={20}
//           shape={(props) => {
//             const { x, y, width, height, value } = props;
//             return (
//               <rect
//                 x={x}
//                 y={value >= 0 ? y : y + height}
//                 width={width}
//                 height={Math.abs(height)}
//                 fill={value >= 0 ? "#16a34a" : "#dc2626"} // green/red
//               />
//             );
//           }}
//         />
//         <Line
//           type="monotone"
//           dataKey="cumulativeReturn"
//           stroke="#3b82f6"
//           strokeWidth={2}
//         />
//       </ComposedChart>
//     </ResponsiveContainer>
//   );
// }


import React, { useState, useEffect } from "react";
import {
  ComposedChart,
  Line,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export default function DailyTradesChart({ data }) {
  const [tickInterval, setTickInterval] = useState(4);

  // Determine tick interval based on screen size
  useEffect(() => {
    const updateInterval = () => {
      const width = window.innerWidth;
      
      if (width < 640) {
        // Mobile: Show ~4-6 labels total (every ~20-30 days for typical dataset)
        setTickInterval(30);
      } else if (width < 1024) {
        // Tablet: Show start of each month (every ~10-15 days)
        setTickInterval(15);
      } else {
        // Desktop: Show more frequent labels
        setTickInterval(4);
      }
    };

    updateInterval();
    window.addEventListener("resize", updateInterval);
    return () => window.removeEventListener("resize", updateInterval);
  }, []);

  if (!data || !data.length) return <p>No trade data available.</p>;

  // Aggregate by day
  const dailyMap = {};
  let cumulative = 0;

  data.forEach((trade) => {
    const date = new Date(trade.trade_date).toISOString().slice(0, 10);
    cumulative += Number(trade.cash_return || 0);

    if (!dailyMap[date]) {
      dailyMap[date] = { date, dailyReturn: 0, cumulativeReturn: 0 };
    }

    dailyMap[date].dailyReturn += Number(trade.cash_return || 0);
    dailyMap[date].cumulativeReturn = cumulative;
  });

  const chartData = Object.values(dailyMap);

  // Custom formatter for Y-axis to add dollar sign
  const formatYAxis = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    } else if (value <= -1000000) {
      return `-$${Math.abs(value / 1000000).toFixed(1)}M`;
    } else if (value <= -1000) {
      return `-$${Math.abs(value / 1000).toFixed(0)}K`;
    }
    return `$${value}`;
  };

  // Custom tick function to show only month starts on smaller screens
  const customXAxisTick = (props) => {
    const { x, y, payload } = props;
    const date = payload.value;
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    
    // On mobile/tablet, only show month starts (1st of month)
    if (window.innerWidth < 1024 && day !== 1) {
      return null;
    }

    // Format: MM/DD or just MM on mobile
    const formattedDate = window.innerWidth < 640
      ? `${dateObj.getMonth() + 1}/${dateObj.getDate()}`
      : date.slice(5); // MM-DD format

    return (
      <text
        x={x}
        y={y}
        dy={16}
        textAnchor="middle"
        fill="#6b7280"
        fontSize={window.innerWidth < 640 ? 10 : 12}
      >
        {formattedDate}
      </text>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const daily = payload.find((p) => p.dataKey === "dailyReturn")?.value || 0;
    const cumulative = payload.find((p) => p.dataKey === "cumulativeReturn")?.value || 0;

    return (
      <div
        className="custom-tooltip"
        style={{ background: "#fff", padding: 10, border: "1px solid #ccc", borderRadius: 4 }}
      >
        <p>
          <strong>{label}</strong>
        </p>
        <p>
          Daily Return:{" "}
          <span style={{ color: daily >= 0 ? "#16a34a" : "#dc2626" }}>
            ${daily.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </p>
        <p>
          Cumulative Return:{" "}
          <span style={{ color: cumulative >= 0 ? "#16a34a" : "#dc2626" }}>
            ${cumulative.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </p>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={chartData}>
        <CartesianGrid vertical={false} stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          interval={tickInterval}
          tick={customXAxisTick}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fill: "#6b7280", fontSize: 12 }}
          tickFormatter={formatYAxis}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar
          dataKey="dailyReturn"
          barSize={20}
          shape={(props) => {
            const { x, y, width, height, value } = props;
            return (
              <rect
                x={x}
                y={value >= 0 ? y : y + height}
                width={width}
                height={Math.abs(height)}
                fill={value >= 0 ? "#16a34a" : "#dc2626"} // green/red
              />
            );
          }}
        />
        <Line
          type="monotone"
          dataKey="cumulativeReturn"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}